import { Box, Card, CardActionArea, CardContent, Container, Skeleton } from '@mui/material';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useClassroomSimulation } from '../../api';
import AppBar from '../../app-bar/AppBar';
import AssessmentResultOverview from '../../assessment-result-overview/AssessmentResultOverview';
import AssessmentScore from '../../assessment-scenarios/AssessmentScore';
import ksimSelfstudyCloud from '../../assets/ksim-self-study-cloud.png';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import Fallback from '../../fallback/Fallback';
import Position from '../../position/Position';
import { apiPaths, queryClient } from '../../queryClient';
import { useSignalREffect } from '../../signalr/SignalRContext';
import SimulationCard from '../../simulation-card/SimulationCard';
import SimulationSkeleton from '../../skeletons/SimulationSkeleton';
import {
  AssessmentUpdatedNotification,
  ExerciseType,
  SimulationEventMessage,
  SimulationRole,
  SimulationStatus,
} from '../../types';
import { reLabelExerciseType } from '../../utils';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

/**
 * Simulation detail page from the instructor's point of view.
 *
 * This page is only for classroom cloud sessions, because those are the only type of simulations that an instructor starts FOR students.
 *
 * The page is visible as long as the simulation is not completed/abandoned.
 * When the classroom session is over, this page isn't relevant anymore and the instructor can see the student simulations on a different route/component.
 */
const Classroom = () => {
  useCheckRole(['Instructor']);

  const { exerciseId, simulationId } = useParams();
  const activeCompany = useActiveCompany();

  const { simulation, simulationLoading, simulationError, updateClassroomSimulation } = useClassroomSimulation(
    exerciseId,
    simulationId,
  );

  useSignalREffect(
    SimulationEventMessage.SimulationCompleted,
    () => {
      queryClient.fetchQuery({
        queryKey: [
          {
            path: apiPaths.teach.classroomSimulation(activeCompany?.companyId, exerciseId, simulationId),
          },
        ],
      });
    },
    [activeCompany],
  );

  useSignalREffect(
    SimulationEventMessage.AssessmentUpdated,
    (...args) => {
      const { assessments, userId } = args[0] as AssessmentUpdatedNotification;
      if (assessments) {
        updateClassroomSimulation({
          ...simulation,
          classroomUsers: simulation?.classroomUsers.map(user => {
            return user.id === userId ? { ...user, assessmentResult: assessments } : user;
          }),
        });
      }
    },
    [simulation],
  );

  if (simulationLoading) {
    return (
      <SimulationSkeleton
        breadcrumbs={[
          { label: 'Home', to: '/teach' },
          { to: '..', label: <Skeleton width={160} /> },
          { label: <Skeleton width={120} /> },
        ]}
      />
    );
  }

  if (simulationError) {
    throw simulationError;
  }

  const {
    endTime,
    exercise: { adHoc, name: exerciseName, type: exerciseType, title: exerciseTitle },
    startTime,
    status,
    simulator: { type: simulatorType },
    classroomUsers,
  } = parseTime(simulation);

  const simulationLink = (classroomUserId: string) =>
    `../../exercises/${exerciseId}/students/${classroomUserId}/simulations/${simulation?.id}`;

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/teach' },
          { to: '..', label: exerciseTitle },
          { label: reLabelExerciseType(exerciseType) },
        ]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container>
        <BackLink label="Back to exercise" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to=".." />

        <SimulationCard
          adHoc={adHoc}
          endTime={endTime}
          exerciseName={exerciseName}
          exerciseType={exerciseType}
          status={status}
          startTime={startTime}
          simulatorType={simulatorType}
        />
        <Box
          display={{ xs: 'flex', md: 'grid' }}
          flexDirection="column"
          gridTemplateColumns={`repeat(${classroomUsers.length >= 2 ? 3 : 2}, 1fr)`}
          gap={2}
          mb={4}
        >
          {classroomUsers?.map((classroomUser, i) => (
            <CardActionArea
              key={i}
              component={Link}
              disabled={classroomUser.classroomRole === SimulationRole.Guest}
              to={simulationLink(classroomUser.id)}
              state={ExerciseType.Classroom}
            >
              <Card>
                <CardContent
                  key={classroomUser.position}
                  sx={{ ':not(:last-child)': { borderBottom: theme => `1px solid ${theme.palette.divider}` } }}
                >
                  <Position
                    assignedStudentAvatar={classroomUser.userImage}
                    assignedLayoutName={classroomUser.layoutName}
                    assignedStudentName={classroomUser.name}
                    classroomRole={classroomUser.classroomRole}
                    name={classroomUser.position}
                  />

                  {classroomUser.assessmentResult?.length ? (
                    <>
                      <AssessmentScore {...classroomUser.assessmentResult[0]} sx={{ mt: 2 }} />
                      <AssessmentResultOverview
                        {...classroomUser.assessmentResult[0]}
                        inProgress={simulation?.status === SimulationStatus.Ongoing}
                        sx={{ gridColumn: '1/4', mt: 2, mb: 2 }}
                      />
                    </>
                  ) : (
                    <Fallback imgSrc={ksimSelfstudyCloud} message={'The assessment will be displayed here'} />
                  )}
                </CardContent>
              </Card>
            </CardActionArea>
          ))}
        </Box>
      </Container>
    </>
  );
};

export default Classroom;
